<template>
  <div
    class="product-showcase"
    :class="{ 'is-visible': show }"
    @mouseenter="$emit('mouseenter')"
    @mouseleave="$emit('mouseleave')"
  >
    <div class="showcase-container" :class="{ isCustom: !isInfo }">
      <div class="showcase-content">
        <!-- STUDIO AI Section -->
        <div class="category-section">
          <h3 class="category-title" v-if="isInfo">
            Spatial AI for content generation from smartphones
          </h3>
          <div class="product-grid">
            <div
              v-for="(product, index) in items"
              :key="product.title"
              v-show="!product?.conversationalProducts?.length"
              @click="handleClick(product.externalLink, product.redirect)"
              class="product-card"
            >
              <div class="card-header">
                <h4 class="product-name">{{ product.title }}</h4>
                <p class="product-desc">{{ product.desc }}</p>
              </div>
              <div
                v-if="product.image"
                class="product-preview"
                :class="{ first: index == 0, third: index == 2 }"
                :style="
                  index != 0 ? { backgroundImage: `url(${product.image})` } : {}
                "
              >
                <DemoViewer
                  v-if="index == 0"
                  pano-id="productShowcase-1"
                  :image-src="`${cdnUrl}/61fcc50f0428aba3fd5dce60/1e408d5c-afad-498b-bdf4-9ab9e6156cd4`"
                />
                <div class="preview-overlay"></div>
                <div v-if="product.tag && false" class="product-tag">
                  {{ product.tag }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- MIRA AI Section -->
        <div
          class="category-section conversational"
          v-if="items[3]?.conversationalProducts?.length"
        >
          <h3 class="category-title" v-if="isInfo">AI Agent</h3>
          <div class="product-grid single">
            <div
              v-for="product in items[3]?.conversationalProducts"
              :key="product.title"
              @click="handleClick(product.externalLink, product.redirect)"
              class="product-card"
            >
              <div class="card-header">
                <h4 class="product-name">
                  <SpeakingAnimation class="speaking-animation" />
                  {{ product.title }}
                </h4>
                <p class="product-desc">{{ product.desc }}</p>
              </div>
              <div class="product-preview vini">
                <MuxVideo
                  muxId="O76Qx02SXeDvzzqCM5A3rbPm6I9uTby5ChGLBzND7Pkc"
                />
                <div class="preview-overlay"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import DemoViewer from "~/components/DemoViewer.vue";
import MuxVideo from "~/components/MuxVideo.vue";
import SpeakingAnimation from "~/components/SpeakingAnimation.vue";
const router = useRouter();
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  isInfo: {
    type: Boolean,
    default: false,
  },
});

const config = useRuntimeConfig();
const cdnUrl = config.public.cdnUrl;

const emit = defineEmits(["mouseenter", "mouseleave", "close", "close-menu"]);

function handleClick(externalLink, redirect) {
  emit("mouseleave");
  emit("close");
  emit("close-menu");
  if (externalLink) {
    window.location.href = redirect;
  } else {
    router.push(`/${redirect}`);
  }
}
</script>

<style lang="scss" scoped>
.arrow-right {
  display: none;
  @include respond-below(sm) {
    display: block;
    margin-block: auto;
  }
}
.product-showcase {
  @extend %blur-background;
  position: fixed;
  top: 2.5rem;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  transform: translateY(-100%);
  transition:
    transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.1s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: -1;

  &.is-visible {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  @include respond-below(lg) {
    position: absolute;
    top: 3.5rem;
    transform: translateY(0);
    z-index: 10000;
    height: calc(100dvh - 36px);
    width: 90vw;
    opacity: 0;
    visibility: hidden;
    border-bottom: none;
    &.is-visible {
      opacity: 1;
      visibility: visible;
    }
  }
}

.showcase-container {
  font-family: $font-primary;
  max-width: 1540px;
  margin: 0 auto;
  padding: 3rem 4rem;
  @include respond-below(sm) {
    padding: 1rem 1rem;
  }
  &.isCustom {
    padding: 2rem 4rem;
    @include respond-below(sm) {
      padding: 1rem 1rem;
    }
    .product-card {
      padding: 1.2rem;
    }
    .card-header {
      margin-bottom: 0rem;
    }
  }
}

.showcase-content {
  display: flex;
  gap: 1.5rem;
  @include respond-below(lg) {
    flex-direction: column;
  }
}

.category-section {
  flex: 3;

  &.conversational {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
}

.category-title {
  filter: contrast(1);
  font-size: 1.1rem;
  font-style: normal;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  margin-bottom: 1.4rem;

  @include respond-below(lg) {
    margin-bottom: 1rem;
    margin-left: 0.3rem;
    font-size: 0.75rem;
  }
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  &.single {
    flex: 1;
    grid-template-columns: 1fr;
  }
  @include respond-below(lg) {
    flex-direction: column;
    grid-template-columns: repeat(1, 1fr);
  }
}

.product-card {
  position: relative;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  display: flex;
  flex-direction: column;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-6px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);

    .product-preview {
      transform: scale(1.02);
      .preview-overlay {
        opacity: 0.1;
      }
    }

    .product-icon {
      color: $theme-primary;
    }

    // .product-name {
    //   color: $theme-primary;
    // }
  }
  @include respond-below(lg) {
    padding: 1rem;
    flex-direction: row-reverse;
    gap: 1rem;
    justify-content: flex-end;
  }
}

.card-header {
  display: flex;
  align-items: start;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.75rem;
  @include respond-below(lg) {
    gap: 0rem;
  }
}

.product-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  transition: color 0.3s ease;
}

.product-name {
  position: relative;
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  @include respond-below(sm) {
    font-size: 0.9rem;
  }
}

.product-desc {
  font-size: 1rem;
  font-style: normal;
  line-height: 1.3;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;

  @include respond-below(lg) {
    margin-top: 0.4rem;
    font-size: 12px;
    line-height: 1.2;
  }
}

.product-preview {
  height: 140px;
  width: 100%;
  border-radius: 12px;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: auto;
  @include respond-below(lg) {
    width: 130px;
    height: 70px;
    margin-top: 0rem;
  }
  &#productShowcase-1 {
    height: 100%;
    width: 100%;
  }
  &.third {
    @include respond-below(lg) {
      width: 100px;
    }
  }
}

.preview-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  transition: opacity 0.4s ease;
}

.product-tag {
  position: absolute;
  top: 12px;
  right: 12px;
  background: $theme-primary;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.vini-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  display: flex;
  justify-content: center;
}

.vini-avatars {
  display: flex;
  gap: -10px;

  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #000;
    object-fit: cover;
    margin-right: -12px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
      z-index: 10;
    }
  }
}
.speaking-animation {
  height: 50px;
  width: 50px;
  position: absolute;
  top: -100%;
  left: -45px;
  @include respond-below(lg) {
    height: 20px !important;
    width: 20px !important;
    left: -20px !important;
    top: -5px !important;
  }
}
</style>
